/**
 * DISTRIBUTOR-ORDERS MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * dashboardSummary — this module's operations-hub (Home) contribution.
 *
 * Why it exists (backlog 240): reference data flowed INTO this module and
 * nothing ever flowed out. It was the only shipped module registering no
 * `dashboardProvider`, so the platform argument the marketing page makes —
 * "…and out to everything it touches" — was literally false here. This is the
 * cheapest true version of that claim: one KPI and three signals, contributed
 * through the documented registry contract rather than by another module
 * reaching into `orders`.
 *
 * Nothing here is imported by anyone: the composition root collects
 * `dashboardProvider` off this module's index.js automatically.
 */

import { loadDashboardOrderStats } from "./ordersService.js";

export async function getDashboardSummary() {
  let s;
  // A provider that throws contributes nothing and is caught by Home — but
  // returning the empty shape keeps the failure quiet rather than sorry-looking.
  try { s = await loadDashboardOrderStats(); } catch { return { kpis: [], signals: [] }; }

  const kpis = [];
  // Only claim the tile when there is something shipped to claim. A brand-new
  // tenant with no orders should see the module's tile, not a row of zeros.
  if (s.shippedYtd > 0) {
    kpis.push({
      key: "orders-shipped",
      label: "Shipped",
      to: "/distributor-orders",
      storageKey: "bc.dash.ordersShippedWindow",
      // YTD leads, not MTD. Shipments land in bursts, so early in a month the
      // MTD figure is legitimately 0 — and a KPI tile reading "0 cases" is
      // indistinguishable from a broken one. MTD is one click away.
      defaultVariant: "ytd",
      variants: [
        { key: "ytd", label: "YTD", display: Math.round(s.shippedYtd).toLocaleString(),
          unit: "cases", sub: "shipped · year to date", status: "neutral" },
        { key: "mtd", label: "MTD", display: Math.round(s.shippedMtd).toLocaleString(),
          unit: "cases", sub: "shipped · this month", status: "neutral" },
      ],
    });
  }

  const signals = [];
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  // These counts are NOT all-time. `loadDashboardOrderStats` bounds the signal
  // query to orders touched in the last 12 months, so an order nobody has looked
  // at since 2024 rolls off and the signal can actually reach zero. The detail
  // lines say so, because a bare "3 orders are on hold" reads as a total and
  // would quietly under-report a genuinely old backlog.
  const RECENT = "worked in the last 12 months";

  if (s.onHold > 0) {
    signals.push({
      key: "orders-on-hold", severity: "warn",
      title: `${plural(s.onHold, "order is", "orders are")} on hold`,
      detail: "On Hold / Review Needed — nothing moves until someone clears them",
      to: "/distributor-orders", actionLabel: "Review",
    });
  }
  if (s.stalePickup > 0) {
    signals.push({
      key: "orders-stale-pickup", severity: "warn",
      title: `${plural(s.stalePickup, "order has", "orders have")} a pickup date in the past`,
      detail: "Still not marked Shipped — either it shipped and nobody said so, or it slipped",
      to: "/distributor-orders", actionLabel: "Open",
    });
  }
  if (s.noTargetMonth > 0) {
    signals.push({
      key: "orders-no-target-month", severity: "info",
      title: `${plural(s.noTargetMonth, "order has", "orders have")} no target ship month`,
      detail: "Missing from the forecast comparison until a month is set",
      to: "/distributor-orders", actionLabel: "Open",
    });
  }

  return { kpis, signals };
}
